import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, RotateCcw, Compass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PATH_FIELDS = [
  { key: "title", label: "Title", type: "input" },
  { key: "public_label", label: "Public label (button copy)", type: "input" },
  { key: "welcome_copy", label: "Welcome copy", type: "textarea" },
  { key: "description", label: "Description", type: "textarea" },
];
const RESULT_FIELDS = [
  { key: "title", label: "Title", type: "input" },
  { key: "summary", label: "Summary", type: "textarea" },
  { key: "guidance", label: "Guidance (a gentle next step)", type: "textarea" },
  { key: "next_step_heading", label: "Next-step heading", type: "input" },
];

export default function GaugeContentPanel() {
  const qc = useQueryClient();
  const [section, setSection] = useState("results");
  const { data, isLoading } = useQuery({ queryKey: ["gauge-content"], queryFn: () => api.get("/admin/gauge/content").then((r) => r.data) });

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#144F75]" size={30} /></div>;

  const items = section === "results" ? data?.results || [] : data?.paths || [];
  const fields = section === "results" ? RESULT_FIELDS : PATH_FIELDS;
  const kind = section === "results" ? "result" : "path";
  const refresh = () => qc.invalidateQueries({ queryKey: ["gauge-content"] });

  return (
    <div data-testid="admin-gauge-content">
      <div className="flex items-center gap-2 mb-2 text-[#144F75]"><Compass size={18} /><h2 className="font-serif text-2xl text-[#1A242B]">GAUGE Exploration Copy</h2></div>
      <p className="text-[#4A5D6B] text-sm mb-6">Edit the wording shown in the exploration. Defaults come from the original GAUGE plugin — your edits override them, and Reset restores the default.</p>

      <div className="flex gap-2 mb-6">
        {[{ k: "results", l: "Results (reflections)" }, { k: "paths", l: "Paths (focus areas)" }, { k: "questions", l: "Questions & Scoring" }].map((s) => (
          <button key={s.k} onClick={() => setSection(s.k)} data-testid={`gauge-content-tab-${s.k}`}
            className={`rounded-full px-4 py-2 text-sm transition-all ${section === s.k ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{s.l}</button>
        ))}
      </div>

      {section === "questions" ? (
        <QuestionsEditor data={data} onDone={refresh} />
      ) : (
        <div className="space-y-4">
          {items.map((item) => <CopyCard key={item.slug} item={item} fields={fields} kind={kind} onDone={refresh} />)}
        </div>
      )}
    </div>
  );
}

function QuestionsEditor({ data, onDone }) {
  const orientations = data?.orientations || ["gratitude", "awareness", "unity", "growth", "energy"];
  const pathOptions = data?.path_options || [];
  const [creating, setCreating] = useState(false);
  const [filterPath, setFilterPath] = useState("");
  const [kind, setKind] = useState("");
  const questions = (data?.questions || []).filter((q) => {
    if (kind === "core" && q.lens) return false;
    if (kind === "deeper" && !q.lens) return false;
    if (kind === "archived" && q.active !== false) return false;
    if (!filterPath) return true;
    const ps = q.paths || [];
    return ps.length === 0 || ps.includes("all") || ps.includes(filterPath);
  });
  return (
    <div data-testid="gauge-questions-editor">
      <div className="flex justify-between items-center mb-3 gap-4 flex-wrap">
        <p className="text-sm text-[#4A5D6B] flex-1 min-w-[260px]">Questions are adaptive and topic-specific: assign each to one or more focus paths (none = every path), tag it for matching, and optionally branch — jump to a specific follow-up when the answer shows struggle or strength.</p>
        <div className="flex items-center gap-2 shrink-0">
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="gauge-q-filter-kind" className="eyw-input text-sm" title="Deeper reflections are optional open questions shown after the result; they never change the check-in level">
            <option value="">All questions</option>
            <option value="core">Core exploration</option>
            <option value="deeper">Deeper reflections</option>
            <option value="archived">Archived</option>
          </select>
          <select value={filterPath} onChange={(e) => setFilterPath(e.target.value)} data-testid="gauge-q-filter-path" className="eyw-input text-sm">
            <option value="">All paths</option>
            {pathOptions.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
          </select>
          <button onClick={() => setCreating(true)} data-testid="gauge-add-question" className="eyw-btn !py-2 text-sm">+ Add question</button>
        </div>
      </div>
      {creating && <QuestionCard question={{ id: "new", question_text: "", helper_text: "", orientation: "awareness", sort_order: 100, is_core: true, active: true, question_type: "frequency", tags: [], paths: filterPath ? [filterPath] : [], follow_up_trigger: "", sense: "", branch_low: "", branch_high: "" }} orientations={orientations} pathOptions={pathOptions} isNew onDone={() => { setCreating(false); onDone(); }} onCancel={() => setCreating(false)} />}
      <div className="space-y-3">
        {questions.map((q) => <QuestionCard key={q.id} question={q} orientations={orientations} pathOptions={pathOptions} onDone={onDone} />)}
      </div>
    </div>
  );
}

function QuestionCard({ question, orientations, pathOptions, isNew, onDone, onCancel }) {
  const [form, setForm] = useState({ ...question });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePath = (slug) => {
    const ps = form.paths || [];
    set("paths", ps.includes(slug) ? ps.filter((p) => p !== slug) : [...ps, slug]);
  };

  const save = async () => {
    if (!form.question_text.trim()) { toast.error("Enter question text"); return; }
    setSaving(true);
    try {
      const body = {
        question_text: form.question_text, helper_text: form.helper_text || "", orientation: form.orientation,
        sort_order: Number(form.sort_order) || 100, is_core: !!form.is_core, active: form.active !== false,
        question_type: form.question_type || "frequency",
        tags: (Array.isArray(form.tags) ? form.tags.join(",") : String(form.tags || "")).split(",").map((t) => t.trim()).filter(Boolean),
        paths: (form.paths || []).filter((p) => p !== "all"),
        follow_up_trigger: form.follow_up_trigger || "", sense: form.sense || "",
        branch_low: String(form.branch_low || ""), branch_high: String(form.branch_high || ""),
        lens: form.lens || "",
      };
      if (isNew) await api.post("/admin/gauge/question", body);
      else await api.put(`/admin/gauge/question/${form.id}`, body);
      toast.success(isNew ? "Question added" : "Question updated");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const assignedPaths = (form.paths || []).filter((p) => p !== "all");

  return (
    <div className="eyw-card p-5" data-testid={`gauge-question-${form.id}`}>
      <div className="flex gap-3 items-start">
        <input value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} data-testid="gauge-q-order" className="eyw-input w-14 text-center" title="Order" />
        <div className="flex-1 space-y-2">
          <textarea rows={2} value={form.question_text} onChange={(e) => set("question_text", e.target.value)} data-testid="gauge-q-text" placeholder="Question text" className="eyw-input w-full resize-none" />
          <input value={form.helper_text || ""} onChange={(e) => set("helper_text", e.target.value)} data-testid="gauge-q-helper" placeholder="Helper text (optional)" className="eyw-input w-full text-sm" />
          <div className="flex flex-wrap gap-2 items-center text-xs text-[#4A5D6B]">
            <label>Shown in
              <select value={form.lens || ""} onChange={(e) => set("lens", e.target.value)} data-testid="gauge-q-lens" className="eyw-input ml-2 text-sm" disabled={!isNew}>
                <option value="">Core exploration</option>
                {["gratitude", "awareness", "unity", "growth", "energy", "support", "returning"].map((l) => <option key={l} value={l}>Deeper: {l}</option>)}
              </select>
            </label>
            {!isNew && <span className="px-2 py-0.5 rounded-full bg-[#144F75]/[0.06]" title={(question.versions || []).length ? `Earlier wording:\n${question.versions.map((v) => `v${v.version}: ${v.question_text}`).join("\n")}` : "Wording changes create a new version so earlier answers keep their original question"} data-testid={`gauge-q-version-${question.id}`}>v{question.version || 1}</span>}
            {question.active === false && <span className="px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]">Archived</span>}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="text-xs text-[#4A5D6B]">Orientation
              <select value={form.orientation} onChange={(e) => set("orientation", e.target.value)} data-testid="gauge-q-orientation" className="eyw-input ml-2 text-sm">
                {orientations.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <label className="text-xs text-[#4A5D6B]">Framing
              <select value={form.sense || ""} onChange={(e) => set("sense", e.target.value)} data-testid="gauge-q-sense" className="eyw-input ml-2 text-sm" title="Concern-framed: answering 'Consistently' means the member is struggling">
                <option value="">Supportive (high = strength)</option>
                <option value="concern">Concern (high = struggle)</option>
              </select>
            </label>
            <label className="text-xs text-[#4A5D6B] flex items-center gap-1.5"><input type="checkbox" checked={form.active !== false} onChange={(e) => set("active", e.target.checked)} data-testid="gauge-q-active" /> Active (shown)</label>
            <button onClick={() => setShowAdvanced((s) => !s)} data-testid="gauge-q-advanced-toggle" className="text-xs text-[#144F75] font-medium hover:underline ml-auto">
              {showAdvanced ? "Hide" : "Topics, tags & branching"} →
            </button>
          </div>
          {(assignedPaths.length > 0 || (form.tags || []).length > 0) && !showAdvanced && (
            <div className="flex flex-wrap gap-1.5">
              {assignedPaths.map((p) => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-[#144F75]/10 text-[#144F75]">{p}</span>)}
              {(Array.isArray(form.tags) ? form.tags : []).map((t) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/10 text-[#5C8A72]">#{t}</span>)}
              {form.follow_up_trigger && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]">follow-up: {form.follow_up_trigger.replace("_", " ")}</span>}
            </div>
          )}
          {showAdvanced && (
            <div className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-4 space-y-3" data-testid="gauge-q-advanced">
              <div>
                <p className="text-xs font-medium text-[#4A5D6B] mb-1.5">Show on paths <span className="font-normal text-[#8FA89B]">(none selected = every path)</span></p>
                <div className="flex flex-wrap gap-1.5">
                  {pathOptions.map((p) => (
                    <button key={p.slug} type="button" onClick={() => togglePath(p.slug)} data-testid={`gauge-q-path-${p.slug}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${assignedPaths.includes(p.slug) ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
                      {p.title}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs text-[#4A5D6B] block">Matching tags (comma-separated)
                  <input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags || ""} onChange={(e) => set("tags", e.target.value)} data-testid="gauge-q-tags" placeholder="stress, restoration" className="eyw-input w-full text-sm mt-1" />
                </label>
                <label className="text-xs text-[#4A5D6B] block">Adaptive role
                  <select value={form.follow_up_trigger || ""} onChange={(e) => set("follow_up_trigger", e.target.value)} data-testid="gauge-q-trigger" className="eyw-input w-full text-sm mt-1">
                    <option value="">Standard question</option>
                    <option value="low_support">Follow-up when member is struggling</option>
                    <option value="strength">Follow-up when member shows strength</option>
                  </select>
                </label>
                <label className="text-xs text-[#4A5D6B] block">If answer shows struggle → jump to question ID
                  <input value={form.branch_low || ""} onChange={(e) => set("branch_low", e.target.value)} data-testid="gauge-q-branch-low" placeholder="e.g. 103" className="eyw-input w-full text-sm mt-1" />
                </label>
                <label className="text-xs text-[#4A5D6B] block">If answer shows strength → jump to question ID
                  <input value={form.branch_high || ""} onChange={(e) => set("branch_high", e.target.value)} data-testid="gauge-q-branch-high" placeholder="e.g. 114" className="eyw-input w-full text-sm mt-1" />
                </label>
              </div>
              <p className="text-[11px] text-[#8FA89B]">Question ID for branching: shown in each card's corner. Branch targets win over the automatic adaptive logic. Note: for concern-framed questions the meaning inverts — answering "Consistently" to "how often are you overwhelmed?" counts as struggle.</p>
            </div>
          )}
        </div>
        <span className="text-[10px] text-[#C9C2B6] shrink-0" title="Question ID (for branching)">#{form.id}</span>
      </div>
      <div className="flex gap-3 mt-3">
        <button onClick={save} disabled={saving} data-testid="gauge-q-save" className="eyw-btn !py-2 text-sm disabled:opacity-60"><Save size={14} /> {isNew ? "Add" : "Save"}</button>
        {isNew && <button onClick={onCancel} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>}
      </div>
    </div>
  );
}

function CopyCard({ item, fields, kind, onDone }) {
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.key, item[f.key] || ""])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/gauge/${kind}/${item.slug}`, { fields: form });
      toast.success("Copy updated.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/gauge/${kind}/${item.slug}`);
      toast.success("Restored to default.");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-6" data-testid={`gauge-copy-${item.slug}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="eyw-label">{item.slug}</p>
        {item.edited && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#B5794E]">Edited</span>}
      </div>
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="text-xs text-[#4A5D6B] mb-1 block">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows={2} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                data-testid={`gauge-copy-${item.slug}-${f.key}`} className="eyw-input w-full resize-none" />
            ) : (
              <input value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                data-testid={`gauge-copy-${item.slug}-${f.key}`} className="eyw-input w-full" />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={save} disabled={saving} data-testid={`gauge-copy-save-${item.slug}`} className="eyw-btn !py-2.5 !px-5 text-sm disabled:opacity-60"><Save size={15} /> Save</button>
        {item.edited && <button onClick={reset} disabled={saving} data-testid={`gauge-copy-reset-${item.slug}`} className="eyw-btn-outline !py-2.5 text-sm"><RotateCcw size={15} /> Reset</button>}
      </div>
    </div>
  );
}
